import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { MentalMinuteSummaryData } from 'teacher-ui-client/components/stat-boxes/fluency-sprints'
import type { SchoolScope, StudentsScope, StudentScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'

export interface MentalMinuteSummaryResponse {
  mental_minute_summary: {
    summary: {
      division: {
        correct_answers: number
        improvement: number
        sprints_mastered: number
      }
      multiplication: {
        correct_answers: number
        improvement: number
        sprints_mastered: number
      }
      addition: {
        correct_answers: number
        improvement: number
        sprints_mastered: number
      }
      subtraction: {
        correct_answers: number
        improvement: number
        sprints_mastered: number
      }
    }
    aggregate: {
      correct_answers: {
        total: number
      }
      improvement: {
        average: number
      }
      sprints_mastered: {
        total: number
      }
    }
  }
}

export async function mentalMinuteSummary(
  this: StudentEventsService,
  product: LegacyProductMs,
  statsScope: SchoolScope | StudentsScope | StudentScope,
  period: NormalizedInterval,
): Promise<Nullable<MentalMinuteSummaryData>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('mental_minute_summary', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<MentalMinuteSummaryResponse>(url, query)
  if (!data) return null

  const {
    mental_minute_summary: { summary: summaryData, aggregate: aggregateData },
  } = data

  const division = summaryData.division
  const multiplication = summaryData.multiplication
  const addition = summaryData.addition
  const subtraction = summaryData.subtraction

  const aggregateCorrectAnswers = aggregateData.correct_answers.total
  const aggregateImprovement = aggregateData.improvement.average
  const aggregateSprintsMastered = aggregateData.sprints_mastered.total

  const emptyAggregate = aggregateCorrectAnswers === 0 && aggregateImprovement === 0 && aggregateSprintsMastered === 0
  if (emptyAggregate) return null

  return {
    summary: {
      division,
      multiplication,
      addition,
      subtraction,
    },
    aggregate: {
      correct_answers: aggregateCorrectAnswers,
      sprints_mastered: aggregateSprintsMastered,
      improvement: aggregateImprovement,
    },
  }
}
